/**
 * COPYRIGHT © 2026 NEXORAX DIGITAL LTD (SC887095). ALL RIGHTS RESERVED.
 * PROPRIETARY NEURAL SYSTEM NODE SYSTEM - PATENT PENDING UNDER SC-NODE-08 / GLOBAL KEY REGISTRY PATTERNS.
 * AUTHORIZED STRICT USE ONLY BY EXECUTIVE DIRECTIVE OF DIRECTOR Lead Director.
 */
import React, { useState, useEffect } from 'react';
import { 
  Database, 
  Plus, 
  Trash2, 
  Edit3, 
  Save, 
  RefreshCw, 
  Layers, 
  Check, 
  X, 
  ShieldAlert, 
  Cpu, 
  Sparkles, 
  Globe, 
  Eye, 
  Sliders,
  Laptop,
  Monitor,
  Tv,
  Mouse,
  Gamepad,
  Wifi,
  Keyboard,
  Settings
} from 'lucide-react';
import { 
  PRODUCTS_50, 
  PRODUCT_CATEGORIES, 
  WebProduct 
} from '../data/productWebsiteData';

export interface HotkeyMapping {
  actionId: string;
  name: string;
  description: string;
  key: string;
  altKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
}

const DEFAULT_HOTKEYS: HotkeyMapping[] = [
  {
    actionId: 'GOTO_MARKETPLACE_MANAGER',
    name: 'Activate Marketplace Console',
    description: 'Switches General dashboard directly to NexoraX Product Database Manager.',
    key: 'm',
    altKey: true,
    ctrlKey: false,
    shiftKey: false
  },
  {
    actionId: 'GOTO_NEXORAX_MARKETPLACE',
    name: 'Toggle NexoraX Marketplace',
    description: 'Instantly opens or closes the NexoraX Marketplace modal view from any visual coordinate.',
    key: 'p',
    altKey: true,
    ctrlKey: false,
    shiftKey: false
  },
  {
    actionId: 'CREATE_NEW_PRODUCT',
    name: 'Deploy New Asset Node',
    description: 'Spins up the creation workflow interface for a new custom catalogue product.',
    key: 'n',
    altKey: true,
    ctrlKey: false,
    shiftKey: false
  },
  {
    actionId: 'EDIT_SELECTED_PRODUCT',
    name: 'Activate Asset Edit Form',
    description: 'Opens the edit and configuration form for the currently selected product node.',
    key: 'e',
    altKey: true,
    ctrlKey: false,
    shiftKey: false
  },
  {
    actionId: 'SAVE_PRODUCT',
    name: 'Authorize Asset Modification',
    description: 'Saves product parameters, updates database, triggers UI sync.',
    key: 's',
    altKey: true,
    ctrlKey: false,
    shiftKey: false
  },
  {
    actionId: 'RESET_PRODUCTS',
    name: 'Revert Default Inventory',
    description: 'Discards custom nodes and loads the default 50 high-fidelity asset catalogue.',
    key: 'r',
    altKey: true,
    ctrlKey: false,
    shiftKey: false
  },
  {
    actionId: 'DESELECT_PRODUCT',
    name: 'Escape Form Focus',
    description: 'Clears focus selection, deselects product, closes the editor slideout.',
    key: 'Escape',
    altKey: false,
    ctrlKey: false,
    shiftKey: false
  }
];

export function ProductDatabaseManager() {
  const [products, setProducts] = useState<WebProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<WebProduct | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isCreatingNew, setIsCreatingNew] = useState<boolean>(false);
  
  // Search and view filters
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('ALL');

  // Form states
  const [formId, setFormId] = useState('');
  const [formName, setFormName] = useState('');
  const [formCodename, setFormCodename] = useState('');
  const [formCategory, setFormCategory] = useState<'Infrastructure' | 'Communications' | 'Neural' | 'Workspace' | 'Security'>('Infrastructure');
  const [formSubcategory, setFormSubcategory] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formPrice, setFormPrice] = useState<number>(0);
  const [formBadge, setFormBadge] = useState('');
  const [formFeatures, setFormFeatures] = useState<string>('');
  const [formGlowColor, setFormGlowColor] = useState('cyan');
  const [formIsHot, setFormIsHot] = useState<boolean>(false);
  const [formImageUrl, setFormImageUrl] = useState('');
  
  // Custom metadata (Specs table)
  const [specKey1, setSpecKey1] = useState('Throughput');
  const [specVal1, setSpecVal1] = useState('NexoraX 0.8s');
  const [specKey2, setSpecKey2] = useState('Encryption');
  const [specVal2, setSpecVal2] = useState('SHA-512 Quantum');
  const [specKey3, setSpecKey3] = useState('Vault Sync');
  const [specVal3, setSpecVal3] = useState('CloudSync-02 Active');

  const [notification, setNotification] = useState<string | null>(null);

  // States for physical hotkeys customizable module
  const [hotkeys, setHotkeys] = useState<HotkeyMapping[]>(() => {
    try {
      const stored = localStorage.getItem('nexorax_admin_hotkeys');
      if (stored) {
        const parsed = JSON.parse(stored) as HotkeyMapping[];
        if (parsed && parsed.length > 0 && parsed[0].actionId) {
          // Merge default mappings with loaded mappings to support dynamic additions
          const merged = [...DEFAULT_HOTKEYS];
          parsed.forEach(storedItem => {
            const index = merged.findIndex(d => d.actionId === storedItem.actionId);
            if (index !== -1) {
              merged[index] = {
                ...merged[index],
                key: storedItem.key,
                altKey: storedItem.altKey,
                ctrlKey: storedItem.ctrlKey,
                shiftKey: storedItem.shiftKey
              };
            }
          });
          return merged;
        }
      }
    } catch (e) {}
    return DEFAULT_HOTKEYS;
  });
  const [listeningActionId, setListeningActionId] = useState<string | null>(null);
  const [showHotkeyModal, setShowHotkeyModal] = useState<boolean>(false);

  const updateHotkeyKey = (actionId: string, newKey: string) => {
    const updated = hotkeys.map(item => {
      if (item.actionId === actionId) {
        return { ...item, key: newKey };
      }
      return item;
    });
    setHotkeys(updated);
    localStorage.setItem('nexorax_admin_hotkeys', JSON.stringify(updated));
    window.dispatchEvent(new Event('nexorax-hotkeys-updated'));
  };

  const toggleHotkeyModifier = (actionId: string, modifierField: 'ctrlKey' | 'altKey' | 'shiftKey') => {
    const updated = hotkeys.map(item => {
      if (item.actionId === actionId) {
        return { ...item, [modifierField]: !item[modifierField] };
      }
      return item;
    });
    setHotkeys(updated);
    localStorage.setItem('nexorax_admin_hotkeys', JSON.stringify(updated));
    window.dispatchEvent(new Event('nexorax-hotkeys-updated'));
  };

  // Listen and capture custom bindings recording state
  useEffect(() => {
    if (!listeningActionId) return;

    const captureKey = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const isModifierOnly = ['alt', 'control', 'shift', 'meta'].includes(e.key.toLowerCase());
      if (isModifierOnly) return;

      const updated = hotkeys.map(mapping => {
        if (mapping.actionId === listeningActionId) {
          return {
            ...mapping,
            key: e.key === ' ' ? 'Space' : e.key,
            altKey: e.altKey,
            ctrlKey: e.ctrlKey,
            shiftKey: e.shiftKey
          };
        }
        return mapping;
      });

      setHotkeys(updated);
      localStorage.setItem('nexorax_admin_hotkeys', JSON.stringify(updated));
      window.dispatchEvent(new Event('nexorax-hotkeys-updated'));
      setListeningActionId(null);
      triggerNotify('SECURITY CONSOLE: Hotkey registry successfully updated & synchronized.');
    };

    window.addEventListener('keydown', captureKey, true);
    return () => window.removeEventListener('keydown', captureKey, true);
  }, [listeningActionId, hotkeys]);

  // Execute actual hotkeys triggers at runtime window context
  useEffect(() => {
    const handleKeys = (e: KeyboardEvent) => {
      if (listeningActionId) return;

      const isInputActive = document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA';

      const matchedMapping = hotkeys.find(mapping => {
        if (isInputActive) {
          if (mapping.key !== 'Escape' && !mapping.altKey && !mapping.ctrlKey) {
            return false;
          }
        }
        const eventKey = e.key.toLowerCase();
        const targetKey = mapping.key.toLowerCase();

        return (
          eventKey === targetKey &&
          e.altKey === mapping.altKey &&
          e.ctrlKey === mapping.ctrlKey &&
          e.shiftKey === mapping.shiftKey
        );
      });

      if (matchedMapping) {
        e.preventDefault();
        e.stopPropagation();

        switch (matchedMapping.actionId) {
          case 'CREATE_NEW_PRODUCT':
            handleStartCreateNew();
            triggerNotify('HOTKEY TRIGGERED: Initiated New Asset deployment flow.');
            break;
          case 'EDIT_SELECTED_PRODUCT':
            if (selectedProduct) {
              setIsEditing(true);
              setIsCreatingNew(false);
              triggerNotify(`HOTKEY TRIGGERED: Entered edit mode for asset node: ${selectedProduct.name}`);
            } else {
              triggerNotify('HOTKEY WARNING: Please select an asset node on the left list first to edit.');
            }
            break;
          case 'GOTO_NEXORAX_MARKETPLACE':
            window.dispatchEvent(new CustomEvent('nexorax-hotkey-action', { detail: { action: 'TOGGLE_MARKETPLACE_VIEW' } }));
            triggerNotify('HOTKEY TRIGGERED: Toggling public NexoraX Marketplace view!');
            break;
          case 'SAVE_PRODUCT': {
            const btn = document.querySelector('form button[type="submit"]') as HTMLButtonElement;
            if (btn) {
              btn.click();
            } else {
              triggerNotify('HOTKEY WARNING: Submission halted, editing form not open.');
            }
            break;
          }
          case 'RESET_PRODUCTS':
            resetToDefaults();
            break;
          case 'DESELECT_PRODUCT':
            setSelectedProduct(null);
            setIsEditing(false);
            setIsCreatingNew(false);
            triggerNotify('HOTKEY TRIGGERED: Closed active focus zone.');
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeys, true);
    return () => window.removeEventListener('keydown', handleKeys, true);
  }, [hotkeys, listeningActionId, products, selectedProduct, isEditing, isCreatingNew]);

  // Initialize and load products
  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = () => {
    try {
      const stored = localStorage.getItem('nexorax_marketplace_products');
      if (stored) {
        setProducts(JSON.parse(stored));
      } else {
        setProducts(PRODUCTS_50);
        localStorage.setItem('nexorax_marketplace_products', JSON.stringify(PRODUCTS_50));
      }
    } catch (e) {
      setProducts(PRODUCTS_50);
    }
  };

  const triggerNotify = (msg: string) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 3000);
  };

  const saveProductsToStorage = (updatedList: WebProduct[]) => {
    localStorage.setItem('nexorax_marketplace_products', JSON.stringify(updatedList));
    setProducts(updatedList);
    // Broadcast change event to refresh Marketplace tab instantly
    window.dispatchEvent(new Event('nexorax-products-updated'));
  };

  const resetToDefaults = () => {
    if (window.confirm('Are you absolute sure you want to revert all custom catalog modifications and restore the 50 default NexoraX high-fidelity assets?')) {
      saveProductsToStorage(PRODUCTS_50);
      setSelectedProduct(null);
      setIsEditing(false);
      setIsCreatingNew(false);
      triggerNotify('MEMBER CONSOLE: Default NexoraX Catalog successfully re-deployed.');
    }
  };

  const handleSelectProduct = (product: WebProduct) => {
    setSelectedProduct(product);
    setIsCreatingNew(false);
    setIsEditing(true);

    // Populate form fields
    setFormId(product.id);
    setFormName(product.name);
    setFormCodename(product.codename);
    setFormCategory(product.category);
    setFormSubcategory(product.subcategory);
    setFormDescription(product.description);
    setFormPrice(product.price);
    setFormBadge(product.badge || '');
    setFormFeatures(product.features.join(', '));
    setFormGlowColor(product.glowColor);
    setFormIsHot(!!product.isHot);
    setFormImageUrl(product.imageUrl || '');
    
    // Specifications extraction
    const currentSpecs = product.specs || {};
    const keys = Object.keys(currentSpecs);
    setSpecKey1(keys[0] || 'Specification A');
    setSpecVal1(currentSpecs[keys[0]] || 'Active Node');
    setSpecKey2(keys[1] || 'Specification B');
    setSpecVal2(currentSpecs[keys[1]] || 'Optimal');
    setSpecKey3(keys[2] || 'Specification C');
    setSpecVal3(currentSpecs[keys[2]] || 'Secure');
  };

  const handleStartCreateNew = () => {
    setSelectedProduct(null);
    setIsEditing(false);
    setIsCreatingNew(true);

    const randomId = `prod-custom-${Math.floor(1000 + Math.random() * 9000)}`;
    setFormId(randomId);
    setFormName('New Neural Interceptor Node');
    setFormCodename('NEX-NET-AURA');
    setFormCategory('Infrastructure');
    setFormSubcategory('Quantum Processing');
    setFormDescription('Proprietary administrative signal routing hardware aligned to the Director office mandates. Real-time latency checks active.');
    setFormPrice(15000);
    setFormBadge('Special Release');
    setFormFeatures('NexoraX Cloud Encrypted, Dual Waveform, GDrive Staged, 0.8s Latency Safe');
    setFormGlowColor('cyan');
    setFormIsHot(true);
    setFormImageUrl('');
    
    setSpecKey1('Standard Mode');
    setSpecVal1('Tactical Edge');
    setSpecKey2('Latency Threshold');
    setSpecVal2('0.8s Mandatory');
    setSpecKey3('NexoraXty Level');
    setSpecVal3('Tier-1 Approved');
  };

  const handleDeleteProduct = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Execute Gavel Strike? Remove this asset permanently from the database?')) {
      const updated = products.filter(p => p.id !== id);
      saveProductsToStorage(updated);
      if (selectedProduct?.id === id) {
        setSelectedProduct(null);
        setIsEditing(false);
      }
      triggerNotify(`SYSTEM TRUNCATED: Asset "${id}" deleted.`);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formName || !formCodename || !formSubcategory || formPrice <= 0) {
      alert('Please fill out all mandatory structural fields.');
      return;
    }

    const featureArray = formFeatures
      .split(',')
      .map(f => f.trim())
      .filter(f => f.length > 0);

    const specsObj: Record<string, string> = {};
    if (specKey1 && specVal1) specsObj[specKey1] = specVal1;
    if (specKey2 && specVal2) specsObj[specKey2] = specVal2;
    if (specKey3 && specVal3) specsObj[specKey3] = specVal3;

    const updatedProduct: WebProduct = {
      id: formId,
      name: formName,
      codename: formCodename,
      category: formCategory,
      subcategory: formSubcategory,
      description: formDescription,
      price: Number(formPrice),
      badge: formBadge,
      stars: 5.0,
      reviews: Math.floor(12 + Math.random() * 88),
      features: featureArray,
      specs: specsObj,
      isHot: formIsHot,
      glowColor: formGlowColor,
      imageUrl: formImageUrl || undefined
    };

    let updatedList: WebProduct[] = [];

    if (isCreatingNew) {
      updatedList = [updatedProduct, ...products];
      setIsCreatingNew(false);
      setSelectedProduct(updatedProduct);
      setIsEditing(true);
      triggerNotify(`ASSET REGISTERED: "${formName}" added successfully.`);
    } else {
      updatedList = products.map(p => p.id === formId ? updatedProduct : p);
      setSelectedProduct(updatedProduct);
      triggerNotify(`LEDGER MODIFIED: "${formName}" updated.`);
    }

    saveProductsToStorage(updatedList);
  };

  // Filter products for admin search
  const filteredList = products.filter(p => {
    const term = searchTerm.toLowerCase();
    const matchSearch = p.name.toLowerCase().includes(term) || 
                        p.id.toLowerCase().includes(term) || 
                        p.codename.toLowerCase().includes(term);
    const matchCat = categoryFilter === 'ALL' || p.category === categoryFilter;
    return matchSearch && matchCat;
  });

  return (
    <div className="bg-[#050711] border border-slate-900 rounded-2xl p-6 shadow-2xl relative overflow-hidden">
      {/* Background neon grid indicators */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,128,255,0.01)_1px,transparent_1px),linear-gradient(90deg,rgba(0,128,255,0.01)_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none" />

      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-slate-800 pb-5 mb-6 relative z-10">
        <div>
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-purple-500 animate-pulse" />
            <span className="text-[10px] font-mono font-bold tracking-[0.3em] text-purple-400 uppercase">
              NODE-08 // MARKETPLACE DIRECT CONTROL
            </span>
          </div>
          <h2 className="text-xl font-sans font-black text-white uppercase tracking-tight flex items-center gap-2 mt-1">
            <Layers className="w-5 h-5 text-purple-500" />
            NexoraX Product Database Console
          </h2>
          <p className="text-slate-500 text-[10px] font-mono uppercase mt-0.5">
            Full read-write deployment configuration pipelines mapped to current website inventories.
          </p>
        </div>

        {/* Global Action Tools */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleStartCreateNew}
            className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-650 hover:from-purple-500 hover:to-indigo-550 text-white font-mono font-bold text-[10px] uppercase tracking-widest rounded-lg transition-all shadow-lg flex items-center gap-2 cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            [ Deploy New Asset Node ]
          </button>
          
          <button
            type="button"
            onClick={() => setShowHotkeyModal(prev => !prev)}
            className={`px-4 py-2 border font-mono font-bold text-[10px] uppercase tracking-widest rounded-lg transition-all flex items-center gap-2 cursor-pointer ${
              showHotkeyModal 
                ? 'bg-purple-900 border-purple-400 text-purple-250 shadow-[0_0_15px_rgba(168,85,247,0.4)]'
                : 'bg-slate-950 border-slate-800 hover:border-purple-900/40 text-purple-400 hover:bg-slate-900'
            }`}
          >
            <Keyboard className="w-3.5 h-3.5" />
            [ Configure Hotkeys ]
          </button>

          <button
            type="button"
            onClick={resetToDefaults}
            className="px-4 py-2 bg-slate-950 hover:bg-slate-900 text-rose-400 hover:text-rose-300 border border-rose-950 hover:border-rose-500/50 font-mono font-bold text-[10px] uppercase tracking-widest rounded-lg transition-all flex items-center gap-2 cursor-pointer"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            [ Revert Catalog ]
          </button>
        </div>
      </div>

      {/* Interactive Neural Hotkey Configuration Console */}
      {showHotkeyModal && (
        <div className="mb-6 bg-gradient-to-r from-[#0a0f1d] to-[#050711] border-2 border-purple-500/30 rounded-xl p-5 relative z-25 overflow-hidden shadow-[0_0_40px_rgba(168,85,247,0.15)] animate-fadeIn">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(168,85,247,0.02)_1px,transparent_1px)] bg-[size:100%_4px] pointer-events-none" />
          
          <div className="flex items-center justify-between border-b border-purple-950 pb-3 mb-4">
            <div className="flex items-center gap-2">
              <div className="p-1 px-1.5 bg-purple-950/40 border border-purple-800 text-purple-300 rounded font-mono text-[9px] font-extrabold animate-pulse">SYSTEM</div>
              <h3 className="text-xs font-mono font-black text-white uppercase tracking-widest flex items-center gap-1.5">
                <Settings className="w-4 h-4 text-purple-400 animate-spin-slow" />
                Cybernetic Hotkey control desk
              </h3>
            </div>
            
            <button 
              type="button"
              onClick={() => {
                setShowHotkeyModal(false);
                setListeningActionId(null);
              }}
              className="text-slate-400 hover:text-white p-1 bg-slate-950 border border-slate-900 hover:border-slate-800 rounded transition-all cursor-pointer"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <p className="text-[10px] text-slate-400 font-mono mb-4 leading-relaxed uppercase">
            Click <strong className="text-purple-400">[ CHANGE BINDING ]</strong> then press any custom sequence of your hardware keyboard (including modifiers <span className="text-cyan-400">Alt, Shift, Ctrl</span>) to auto-bind. Standard inputs are protected.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {hotkeys.map((h) => {
              const isListening = listeningActionId === h.actionId;
              
              const modifiers = [];
              if (h.ctrlKey) modifiers.push('Ctrl');
              if (h.altKey) modifiers.push('Alt');
              if (h.shiftKey) modifiers.push('Shift');
              
              const keyDisplay = h.key.length === 1 ? h.key.toUpperCase() : h.key;
              const fullTriggerSymbol = [...modifiers, keyDisplay].join(' + ');

              return (
                <div 
                  key={h.actionId}
                  className={`p-3.5 border rounded-xl flex flex-col justify-between gap-3 transition-colors ${
                    isListening 
                      ? 'bg-rose-950/20 border-rose-500/50 shadow-[0_0_15px_rgba(244,63,94,0.15)]' 
                      : 'bg-black/50 border-slate-900 hover:border-slate-800/80'
                  }`}
                >
                  <div className="text-left flex-1">
                    <div className="flex items-center justify-between gap-1">
                      <span className="font-mono text-[10px] font-black text-purple-300 uppercase tracking-wide">
                        {h.name}
                      </span>
                      {(h.actionId === 'GOTO_MARKETPLACE_MANAGER' || h.actionId === 'GOTO_NEXORAX_MARKETPLACE') && (
                        <span className="text-[7.5px] font-mono font-extrabold text-cyan-400 bg-cyan-950/30 px-1 py-0.2 border border-cyan-800/40 rounded uppercase shrink-0">Global Link</span>
                      )}
                    </div>
                    <p className="text-[9px] text-slate-500 font-sans mt-0.5 uppercase leading-snug">
                      {h.description}
                    </p>

                    {/* Interactive Manual Modifier & Key Edit Tool */}
                    <div className="mt-3 pt-2.5 border-t border-slate-900/60 flex flex-col gap-2">
                      <span className="text-[8px] font-mono font-bold text-purple-400 uppercase tracking-widest flex items-center gap-1 leading-none select-none">
                        <Settings className="w-2.5 h-2.5 text-purple-400 animate-spin-slow" /> Direct Edit Tool:
                      </span>
                      <div className="flex items-center gap-1.5">
                        <div className="flex-1 min-w-0">
                          <input
                            type="text"
                            value={h.key}
                            onChange={(e) => updateHotkeyKey(h.actionId, e.target.value)}
                            placeholder="Key"
                            className="w-full px-2 py-1 bg-slate-950/80 hover:bg-black border border-slate-800 focus:border-purple-600 rounded font-mono text-[9px] text-[#00ff22] placeholder-slate-700 transition-colors uppercase outline-none"
                            title="Directly edit key trigger"
                          />
                        </div>

                        <div className="flex items-center gap-0.5 shrink-0">
                          <button
                            type="button"
                            onClick={() => toggleHotkeyModifier(h.actionId, 'ctrlKey')}
                            className={`px-1 py-0.5 text-[7.5px] font-mono rounded border transition-colors cursor-pointer ${
                              h.ctrlKey 
                                ? 'bg-purple-900 border-purple-400 text-purple-200' 
                                : 'bg-slate-950 border-slate-900 text-slate-500 hover:text-slate-300'
                            }`}
                            title="Ctrl Modifier"
                          >
                            Ctrl
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleHotkeyModifier(h.actionId, 'altKey')}
                            className={`px-1 py-0.5 text-[7.5px] font-mono rounded border transition-colors cursor-pointer ${
                              h.altKey 
                                ? 'bg-purple-900 border-purple-400 text-purple-200' 
                                : 'bg-slate-950 border-slate-900 text-slate-500 hover:text-slate-300'
                            }`}
                            title="Alt Modifier"
                          >
                            Alt
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleHotkeyModifier(h.actionId, 'shiftKey')}
                            className={`px-1 py-0.5 text-[7.5px] font-mono rounded border transition-colors cursor-pointer ${
                              h.shiftKey 
                                ? 'bg-purple-900 border-purple-400 text-purple-200' 
                                : 'bg-slate-950 border-slate-900 text-slate-500 hover:text-slate-300'
                            }`}
                            title="Shift Modifier"
                          >
                            Shift
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-2 border-t border-slate-900 pt-2 bg-slate-950/50 -mx-3.5 -mb-3.5 p-3 rounded-b-xl shrink-0">
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="text-[8px] font-mono text-slate-500 uppercase font-bold">BIND:</span>
                      <kbd className="inline-block px-1.5 py-0.5 bg-slate-900 border border-slate-800 rounded font-mono text-[9px] text-[#00ff22] font-black tracking-wide shadow-sm">
                        {fullTriggerSymbol || 'None'}
                      </kbd>
                    </div>

                    <button
                      type="button"
                      onClick={() => setListeningActionId(isListening ? null : h.actionId)}
                      className={`px-2 py-1 select-none font-mono text-[8px] uppercase font-black tracking-wider rounded transition-all cursor-pointer border ${
                        isListening
                          ? 'bg-rose-600 hover:bg-rose-500 border-rose-400 text-black animate-pulse'
                          : 'bg-purple-950/30 border-purple-800/60 hover:border-purple-500 text-purple-300 hover:bg-purple-900/20'
                      }`}
                    >
                      {isListening ? '[ LISTENING... ]' : '[ RECORD TRIGGER ]'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between mt-4 pt-3.5 border-t border-slate-900">
            <span className="text-[8.5px] font-mono text-slate-600 uppercase font-extrabold flex items-center gap-1">
              <ShieldAlert className="w-3.5 h-3.5 text-slate-600" /> ACTIVE GUARDIAN NODE INTERCEPTORS ACTIVE
            </span>
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Revert all shortcuts to factory standard?')) {
                  setHotkeys(DEFAULT_HOTKEYS);
                  localStorage.setItem('nexorax_admin_hotkeys', JSON.stringify(DEFAULT_HOTKEYS));
                  window.dispatchEvent(new Event('nexorax-hotkeys-updated'));
                  setListeningActionId(null);
                  triggerNotify('SECURITY CONSOLE: Shortcuts reverted to factory standard mappings.');
                }
              }}
              className="px-2.5 py-1 text-[8.5px] font-mono font-bold text-slate-500 hover:text-rose-400 transition-colors uppercase cursor-pointer"
            >
              [ Restore default mappings ]
            </button>
          </div>
        </div>
      )}

      {/* Real-time system notifications inside block */}
      {notification && (
        <div className="mb-4 p-3 bg-purple-950/20 border border-purple-500/30 text-purple-300 rounded-lg text-xs font-mono flex items-center gap-2 animate-bounce">
          <Sparkles className="w-4 h-4 text-purple-400 animate-pulse" />
          <span>{notification}</span>
        </div>
      )}

      {/* Main Panel Content Split */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 relative z-10">
        
        {/* Left Side: Product database listing */}
        <div className="xl:col-span-5 bg-black/60 border border-slate-900 rounded-xl p-4 flex flex-col gap-4 h-[650px]">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-mono font-black text-slate-300 uppercase tracking-widest">
              Live Directories ({products.length})
            </h3>
            <span className="text-[10px] font-mono px-2 py-0.5 bg-slate-900 border border-slate-800 rounded text-slate-500 font-extrabold">
              0.8s SYNC ACTIVE
            </span>
          </div>

          {/* Quick Search and Filters */}
          <div className="grid grid-cols-2 gap-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Find asset..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded px-2.5 py-1.5 font-mono text-[9px] uppercase tracking-wider text-white placeholder-slate-600 focus:outline-none focus:border-purple-500/50"
              />
            </div>
            <div>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded px-2.5 py-1.5 font-mono text-[9px] uppercase tracking-wider text-slate-400 focus:outline-none focus:border-purple-500/50 cursor-pointer"
              >
                <option value="ALL">ALL CATEGORIES</option>
                <option value="Infrastructure">Infrastructure</option>
                <option value="Communications">Communications</option>
                <option value="Neural">Neural</option>
                <option value="Workspace">Workspace</option>
                <option value="Security">Security</option>
              </select>
            </div>
          </div>

          {/* Product Cards List container */}
          <div className="flex-1 overflow-y-auto pr-1.5 space-y-2 custom-scrollbar">
            {filteredList.map((product) => {
              const isActive = selectedProduct?.id === product.id;
              
              // Map colors to visual badges safely
              const badgeColors: Record<string, string> = {
                cyan: 'border-cyan-500/30 text-cyan-400 bg-cyan-950/25',
                purple: 'border-purple-500/30 text-purple-400 bg-purple-950/25',
                rose: 'border-rose-500/30 text-rose-400 bg-rose-950/25',
                indigo: 'border-indigo-500/30 text-indigo-400 bg-indigo-950/25',
                emerald: 'border-emerald-500/30 text-emerald-400 bg-emerald-950/25',
                amber: 'border-amber-500/30 text-amber-400 bg-amber-950/25',
              };

              const badgeColor = badgeColors[product.glowColor] || 'border-slate-800 text-slate-400 bg-slate-950';

              return (
                <div
                  key={product.id}
                  onClick={() => handleSelectProduct(product)}
                  className={`p-3 border rounded-xl flex items-center justify-between gap-4 cursor-pointer transition-all ${
                    isActive 
                      ? 'bg-purple-950/15 border-purple-500/50 shadow-[0_0_15px_rgba(168,85,247,0.15)]' 
                      : 'bg-black/40 border-slate-900 hover:border-slate-800 hover:bg-slate-950/35'
                  }`}
                >
                  <div className="flex items-center gap-3 overflow-hidden">
                    <div className={`p-2 rounded-lg border ${badgeColor} shrink-0`}>
                      {product.codename.includes('LAPTOP') || product.codename.includes('CORE') ? (
                        <Laptop className="w-4 h-4 shrink-0" />
                      ) : product.codename.includes('AURORA') || product.codename.includes('DECK') ? (
                        <Monitor className="w-4 h-4 shrink-0" />
                      ) : product.codename.includes('VR') ? (
                        <Tv className="w-4 h-4 shrink-0" />
                      ) : product.codename.includes('MOUSE') ? (
                        <Mouse className="w-4 h-4 shrink-0" />
                      ) : product.codename.includes('CTRL') ? (
                        <Gamepad className="w-4 h-4 shrink-0" />
                      ) : product.codename.includes('NET-AURA') || product.codename.includes('FIBER') ? (
                        <Wifi className="w-4 h-4 shrink-0" />
                      ) : (
                        <Cpu className="w-4 h-4 shrink-0" />
                      )}
                    </div>
                    <div className="truncate text-left">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-[9px] text-[#00ffff] font-extrabold uppercase shrink-0">
                          {product.codename}
                        </span>
                        {product.isHot && (
                          <span className="inline-block px-1 py-0.5 bg-rose-950 text-rose-400 border border-rose-900 rounded-[3px] text-[7px] font-mono leading-none font-bold">HOT</span>
                        )}
                      </div>
                      <h4 className="text-[11px] font-sans font-black text-slate-100 uppercase truncate">
                        {product.name}
                      </h4>
                      <div className="flex items-center gap-2 mt-0.5 text-[8.5px] font-mono text-slate-500">
                        <span>{product.category}</span>
                        <span>•</span>
                        <span>£{product.price.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={(e) => handleDeleteProduct(product.id, e)}
                    className="p-1 px-1.5 hover:bg-rose-950/40 border border-transparent hover:border-rose-900 text-slate-600 hover:text-rose-400 rounded transition-all shrink-0 cursor-pointer"
                    title="Gavel Strike - Delete Product"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Side: Product Creation / Modification Panel */}
        <div className="xl:col-span-7 flex flex-col gap-4">
          
          {!(isEditing || isCreatingNew) ? (
            <div className="bg-black/40 border border-slate-900 rounded-xl p-10 h-[650px] flex flex-col items-center justify-center text-center gap-4 relative">
              <Database className="w-16 h-16 text-slate-700 animate-pulse" />
              <h3 className="text-sm font-mono font-bold tracking-widest text-slate-400 uppercase">
                Awaiting Administrative Address Range
              </h3>
              <p className="text-slate-500 text-xs font-mono max-w-sm leading-relaxed">
                No active asset node targeted. Please select any product node from the directories on the left, or deploy a new custom system.
              </p>
              <button
                type="button"
                onClick={handleStartCreateNew}
                className="mt-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-mono font-black text-[10px] uppercase tracking-widest rounded-lg transition-all shadow-md cursor-pointer"
              >
                [ DEPLOY NEW ASSET NODE ]
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 h-[650px] overflow-y-auto pr-1">
              
              {/* Form Config Block */}
              <div className="lg:col-span-7 bg-[#070914] border border-slate-900 rounded-xl p-5 flex flex-col gap-4">
                <div className="flex items-center justify-between border-b border-slate-900 pb-3">
                  <div className="flex items-center gap-2">
                    <Sliders className="w-4 h-4 text-[#00ffff]" />
                    <span className="text-xs font-mono font-bold uppercase tracking-widest text-[#00ffff]">
                      {isCreatingNew ? 'Create New System Node' : 'Configure Targeted Node'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditing(false);
                      setIsCreatingNew(false);
                      setSelectedProduct(null);
                    }}
                    className="p-1 hover:bg-slate-900 border border-slate-900 hover:border-slate-800 rounded transition-all cursor-pointer"
                  >
                    <X className="w-3.5 h-3.5 text-slate-400" />
                  </button>
                </div>

                <form onSubmit={handleFormSubmit} className="space-y-4 text-xs">
                  {/* Row: ID & Codename */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1 text-left">
                      <label className="font-mono text-[9px] font-bold text-slate-500 uppercase tracking-widest">
                        Node Anchor Registry ID (Read-only)
                      </label>
                      <input
                        type="text"
                        value={formId}
                        readOnly
                        className="bg-slate-950 border border-slate-900 rounded p-2 text-slate-500 font-mono tracking-widest uppercase text-[10px]"
                      />
                    </div>
                    <div className="flex flex-col gap-1 text-left">
                      <label className="font-mono text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                        Product Codename *
                      </label>
                      <input
                        type="text"
                        value={formCodename}
                        onChange={(e) => setFormCodename(e.target.value)}
                        placeholder="e.g. NEX-CORE-X"
                        className="bg-slate-950 border border-slate-800 rounded p-2 text-white font-mono tracking-widest uppercase focus:outline-none focus:border-purple-500/50"
                        required
                      />
                    </div>
                  </div>

                  {/* Row: Name */}
                  <div className="flex flex-col gap-1 text-left">
                    <label className="font-mono text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                      Asset Name *
                    </label>
                    <input
                      type="text"
                      value={formName}
                      onChange={(e) => setFormName(e.target.value)}
                      placeholder="e.g. NexoraX Quantum Processor"
                      className="bg-slate-950 border border-slate-800 rounded p-2 text-white font-sans font-bold uppercase focus:outline-none focus:border-purple-500/50"
                      required
                    />
                  </div>

                  {/* Row: Category & Subcategory */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1 text-left">
                      <label className="font-mono text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                        Corporate Sector Category *
                      </label>
                      <select
                        value={formCategory}
                        onChange={(e) => setFormCategory(e.target.value as any)}
                        className="bg-slate-950 border border-slate-800 rounded p-2 text-white font-mono focus:outline-none focus:border-purple-500/50 cursor-pointer"
                        required
                      >
                        <option value="Infrastructure">Infrastructure</option>
                        <option value="Communications">Communications</option>
                        <option value="Neural">Neural</option>
                        <option value="Workspace">Workspace</option>
                        <option value="Security">Security</option>
                      </select>
                    </div>
                    <div className="flex flex-col gap-1 text-left">
                      <label className="font-mono text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                        Subcategory Matrix *
                      </label>
                      <input
                        type="text"
                        value={formSubcategory}
                        onChange={(e) => setFormSubcategory(e.target.value)}
                        placeholder="e.g. Quantum Processing"
                        className="bg-slate-950 border border-slate-800 rounded p-2 text-white font-mono focus:outline-none focus:border-purple-500/50"
                        required
                      />
                    </div>
                  </div>

                  {/* Row: Price & Custom Badge */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1 text-left">
                      <label className="font-mono text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                        Direct Settlement Price (GBP) *
                      </label>
                      <input
                        type="number"
                        value={formPrice}
                        onChange={(e) => setFormPrice(Number(e.target.value))}
                        className="bg-slate-950 border border-slate-800 rounded p-2 text-white font-mono font-bold focus:outline-none focus:border-purple-500/50"
                        required
                        min="1"
                      />
                    </div>
                    <div className="flex flex-col gap-1 text-left">
                      <label className="font-mono text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                        Staging Badge Text
                      </label>
                      <input
                        type="text"
                        value={formBadge}
                        onChange={(e) => setFormBadge(e.target.value)}
                        placeholder="e.g. Director Selection"
                        className="bg-slate-950 border border-slate-800 rounded p-2 text-[#00ff22] font-mono uppercase focus:outline-none focus:border-purple-500/50"
                      />
                    </div>
                  </div>

                  {/* Description Box */}
                  <div className="flex flex-col gap-1 text-left">
                    <label className="font-mono text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                      NexoraX Mission Description
                    </label>
                    <textarea
                      rows={3}
                      value={formDescription}
                      onChange={(e) => setFormDescription(e.target.value)}
                      placeholder="Input the functional description or spec overview..."
                      className="bg-slate-950 border border-slate-800 rounded p-2 text-slate-300 font-sans focus:outline-none focus:border-purple-500/50 resize-none"
                    />
                  </div>

                  {/* Features List */}
                  <div className="flex flex-col gap-1 text-left">
                    <label className="font-mono text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                      Key Capabilities (Comma Separated Features)
                    </label>
                    <input
                      type="text"
                      value={formFeatures}
                      onChange={(e) => setFormFeatures(e.target.value)}
                      placeholder="e.g. Cryo-Cooling, Quantum Superposition, 0.8s Approved"
                      className="bg-slate-950 border border-slate-800 rounded p-2 text-white font-mono text-[10px] focus:outline-none focus:border-purple-500/50"
                    />
                  </div>

                  {/* Specifications Grid */}
                  <div className="flex flex-col gap-2 border border-slate-900 rounded p-3 text-left">
                    <span className="font-mono text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                      Hardware Specifications Matrix (Specs)
                    </span>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex gap-1.5">
                        <input
                          type="text"
                          value={specKey1}
                          onChange={(e) => setSpecKey1(e.target.value)}
                          placeholder="Key 1"
                          className="w-1/2 bg-slate-950 border border-slate-900 rounded p-1 font-mono text-[9px] text-slate-500"
                        />
                        <input
                          type="text"
                          value={specVal1}
                          onChange={(e) => setSpecVal1(e.target.value)}
                          placeholder="Value 1"
                          className="w-1/2 bg-slate-950 border border-slate-900 rounded p-1 font-mono text-[9px] text-[#00ffff]"
                        />
                      </div>
                      <div className="flex gap-1.5">
                        <input
                          type="text"
                          value={specKey2}
                          onChange={(e) => setSpecKey2(e.target.value)}
                          placeholder="Key 2"
                          className="w-1/2 bg-slate-950 border border-slate-900 rounded p-1 font-mono text-[9px] text-slate-500"
                        />
                        <input
                          type="text"
                          value={specVal2}
                          onChange={(e) => setSpecVal2(e.target.value)}
                          placeholder="Value 2"
                          className="w-1/2 bg-slate-950 border border-slate-900 rounded p-1 font-mono text-[9px] text-[#00ffff]"
                        />
                      </div>
                      <div className="flex gap-1.5 col-span-2">
                        <input
                          type="text"
                          value={specKey3}
                          onChange={(e) => setSpecKey3(e.target.value)}
                          placeholder="Key 3"
                          className="w-1/2 bg-slate-950 border border-slate-900 rounded p-1 font-mono text-[9px] text-slate-500"
                        />
                        <input
                          type="text"
                          value={specVal3}
                          onChange={(e) => setSpecVal3(e.target.value)}
                          placeholder="Value 3"
                          className="w-1/2 bg-[#0d0a1c] border border-slate-900 rounded p-1 font-mono text-[9px] text-purple-400"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Image, Glow & Hot Settings */}
                  <div className="grid grid-cols-2 gap-3 items-center">
                    
                    {/* Visual Color Picker */}
                    <div className="flex flex-col gap-1 text-left">
                      <label className="font-mono text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                        Interactive Visual Glow Aura
                      </label>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {['cyan', 'purple', 'rose', 'indigo', 'emerald', 'amber'].map((col) => {
                          const colClass: Record<string, string> = {
                            cyan: 'bg-cyan-500 shadow-[0_0_10px_rgba(6,182,212,0.8)]',
                            purple: 'bg-purple-500 shadow-[0_0_10px_rgba(168,85,247,0.8)]',
                            rose: 'bg-rose-500 shadow-[0_0_10px_rgba(244,63,94,0.8)]',
                            indigo: 'bg-indigo-500 shadow-[0_0_10px_rgba(99,102,241,0.8)]',
                            emerald: 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.8)]',
                            amber: 'bg-amber-500 shadow-[0_0_10px_rgba(245,158,11,0.8)]',
                          };
                          const isSel = formGlowColor === col;
                          return (
                            <button
                              key={col}
                              type="button"
                              onClick={() => setFormGlowColor(col)}
                              className={`w-4 h-4 rounded-full border ${isSel ? 'border-white scale-125' : 'border-transparent'} ${colClass[col]} transition-transform duration-100 cursor-pointer`}
                              title={col}
                            />
                          );
                        })}
                      </div>
                    </div>

                    {/* Checkbox settings */}
                    <div className="flex items-center gap-4 text-left justify-end">
                      <div className="flex items-center gap-1.5">
                        <input
                          type="checkbox"
                          id="formIsHot"
                          checked={formIsHot}
                          onChange={(e) => setFormIsHot(e.target.checked)}
                          className="w-4.5 h-4.5 accent-purple-600 rounded bg-slate-950 border-slate-800 cursor-pointer"
                        />
                        <label htmlFor="formIsHot" className="font-mono text-[9px] font-bold text-rose-400 uppercase tracking-wider cursor-pointer">
                          🔥 SET SEGMENT HOT
                        </label>
                      </div>
                    </div>

                  </div>

                  {/* Image URL (Optional) */}
                  <div className="flex flex-col gap-1 text-left">
                    <label className="font-mono text-[9px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1">
                      Custom URL Graphic (Optional, defaults to Node Vector outline)
                    </label>
                    <input
                      type="text"
                      value={formImageUrl}
                      onChange={(e) => setFormImageUrl(e.target.value)}
                      placeholder="e.g. https://images.unsplash.com/photo-xxx"
                      className="bg-slate-950 border border-slate-800 rounded p-2 text-white font-mono text-[10px] focus:outline-none focus:border-purple-500/50"
                    />
                  </div>

                  {/* Command Deployment Button */}
                  <button
                    type="submit"
                    className="w-full py-3 bg-[#a855f7] hover:bg-[#b56df9] text-black font-mono font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow-[0_0_20px_rgba(168,85,247,0.3)] hover:shadow-[0_0_30px_rgba(168,85,247,0.55)] cursor-pointer flex items-center justify-center gap-2"
                  >
                    <Check className="w-4 h-4" />
                    {isCreatingNew ? '[ COMMISSION SECURITY DEPLOYMENT ]' : '[ AUTHORIZE MATRIX MODIFICATION ]'}
                  </button>

                </form>
              </div>

              {/* Real-time Renderer Model Selector Preview */}
              <div className="lg:col-span-5 flex flex-col gap-4">
                <div className="bg-[#04060e] border border-slate-900 rounded-xl p-4 flex flex-col justify-between flex-1 relative min-h-[400px]">
                  
                  {/* Top Header info */}
                  <div className="flex items-center justify-between border-b border-slate-900/60 pb-2.5">
                    <span className="text-[9px] font-mono text-slate-500 tracking-wider">HOLOGRAPHIC DIAGNOSTICS</span>
                    <span className="text-[10px] font-mono text-purple-400 font-extrabold flex items-center gap-1 animate-pulse">
                      <Eye className="w-3.5 h-3.5" /> RE-CALIBRATING
                    </span>
                  </div>

                  {/* Virtual Node Render visual overlay */}
                  <div className="flex-1 my-5 bg-gradient-to-t from-slate-950 to-black border border-slate-900/80 rounded-lg flex flex-col items-center justify-center relative shadow-[inset_0_0_30px_rgba(0,0,0,1)] overflow-hidden">
                    
                    {/* Floating neon light coordinate points */}
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,255,255,0.02),transparent)]" />
                    
                    {/* Custom glow indicator according to the picked glow color */}
                    <div className={`absolute w-36 h-36 rounded-full blur-[45px] opacity-25 animate-pulse`} 
                      style={{
                        backgroundColor: 
                          formGlowColor === 'cyan' ? '#00ffff' :
                          formGlowColor === 'purple' ? '#a855f7' :
                          formGlowColor === 'rose' ? '#f43f5e' :
                          formGlowColor === 'indigo' ? '#6366f1' :
                          formGlowColor === 'emerald' ? '#10b981' :
                          formGlowColor === 'amber' ? '#f59e0b' : '#a855f7'
                      }}
                    />

                    {/* Spinner element */}
                    <div className="relative p-5 bg-black/45 border border-slate-800 rounded-full flex items-center justify-center">
                      {formCodename.includes('LAPTOP') || formCodename.includes('CORE') ? (
                        <Laptop className="w-12 h-12 text-[#00ffff] animate-pulse" />
                      ) : formCodename.includes('AURORA') || formCodename.includes('DECK') ? (
                        <Monitor className="w-12 h-12 text-cyan-200 animate-pulse" />
                      ) : formCodename.includes('VR') ? (
                        <Tv className="w-12 h-12 text-[#a855f7] animate-pulse" />
                      ) : formCodename.includes('MOUSE') ? (
                        <Mouse className="w-12 h-12 text-emerald-400 animate-pulse" />
                      ) : formCodename.includes('CTRL') ? (
                        <Gamepad className="w-12 h-12 text-rose-400 animate-pulse" />
                      ) : formCodename.includes('NET-AURA') || formCodename.includes('FIBER') ? (
                        <Wifi className="w-12 h-12 text-[#e840f3] animate-pulse" />
                      ) : (
                        <Cpu className="w-12 h-12 text-[#00ff22] animate-pulse" />
                      )}
                      
                      {/* Interactive ping status dots */}
                      <span className="absolute -top-1.5 -right-1.5 w-3 h-3 rounded-full bg-emerald-500 animate-ping" />
                    </div>

                    <div className="absolute inset-x-0 h-0.5 bg-gradient-to-r from-transparent via-[#a855f7]/30 to-transparent animate-bounce top-1/3 pointer-events-none" />

                    <div className="absolute bottom-3 left-4 text-left font-mono text-[8px] text-slate-500 uppercase">
                      NAME: <strong className="text-white">{formName || 'N/A'}</strong><br />
                      CODE: <span className="text-purple-400">{formCodename || 'N/A'}</span>
                    </div>

                    <div className="absolute bottom-3 right-4 text-right font-mono text-[8px] text-slate-500 uppercase">
                      Price: <strong className="text-amber-400">£{formPrice.toLocaleString()}</strong><br />
                      Aerosol Glow: <span className="text-[#00ffff]">{formGlowColor}</span>
                    </div>

                  </div>

                  {/* Specifications Read-only diagnostic view */}
                  <div className="bg-[#050711] border border-slate-900 rounded-lg p-3 text-left space-y-2">
                    <span className="font-mono text-[8px] text-slate-500 tracking-[0.2em] font-extrabold uppercase">
                      ACTIVE DEPLOYMENT SPECS
                    </span>
                    <div className="grid grid-cols-3 gap-2 text-center text-[9px] font-mono uppercase">
                      <div className="bg-black/60 border border-slate-900 p-1.5 rounded">
                        <span className="block text-slate-600 font-extrabold text-[7px] tracking-wider">{specKey1}</span>
                        <strong className="text-slate-300 truncate block mt-0.5">{specVal1 || 'None'}</strong>
                      </div>
                      <div className="bg-black/60 border border-slate-900 p-1.5 rounded">
                        <span className="block text-slate-600 font-extrabold text-[7px] tracking-wider">{specKey2}</span>
                        <strong className="text-[#00ffff] truncate block mt-0.5">{specVal2 || 'None'}</strong>
                      </div>
                      <div className="bg-black/60 border border-slate-900 p-1.5 rounded">
                        <span className="block text-slate-600 font-extrabold text-[7px] tracking-wider">{specKey3}</span>
                        <strong className="text-purple-400 truncate block mt-0.5">{specVal3 || 'None'}</strong>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

            </div>
          )}

        </div>

      </div>

    </div>
  );
}
